import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle, ClipboardCheck, RotateCcw, Loader2, Trophy, RefreshCw } from "lucide-react";

const LETTERS = ["A", "B", "C", "D", "E", "F"];

export function Quiz({ assessment, lastAttempt, preview = false }) {
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const questions = assessment.questions || [];

  const submit = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error("Please answer every question first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/assessments/${assessment.assessment_id}/submit`, { answers, preview });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setAnswers({}); setResult(null); };

  if (!open) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border-2 border-border bg-card p-5 shadow-sm sm:p-6" data-testid={`quiz-card-${assessment.assessment_id}`}>
        <div className="flex items-center gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-secondary"><ClipboardCheck className="h-7 w-7" strokeWidth={2.5} /></span>
          <div>
            <p className="font-head text-lg font-black">{assessment.title}</p>
            <p className="text-sm font-bold text-muted-foreground">{questions.length} questions · {assessment.total_points || 0} pts · pass {assessment.passing_score || 70}%</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {assessment.printable && (
            <Button onClick={() => window.print()} variant="outline" className="rounded-full border-2 font-extrabold" data-testid={`quiz-print-${assessment.assessment_id}`}>Print</Button>
          )}
          {lastAttempt && (
            <span className={`rounded-full px-3 py-1.5 text-sm font-extrabold ${lastAttempt.passed ? "bg-green-100 text-green-600" : "bg-muted text-muted-foreground"}`}>
              Last: {lastAttempt.score_pct}% {lastAttempt.passed ? "· Passed" : ""}
            </span>
          )}
          <Button onClick={() => setOpen(true)} className="rounded-full bg-primary px-7 py-6 text-base font-extrabold text-white tactile hover:bg-primary" data-testid={`quiz-start-${assessment.assessment_id}`}>
            {lastAttempt ? "Retake Quiz" : "Take Quiz"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border-2 border-border bg-card p-6 shadow-sm sm:p-8" data-testid={`quiz-open-${assessment.assessment_id}`}>
      <div className="flex items-center justify-between border-b-2 border-border pb-4">
        <h3 className="font-head text-xl font-black">{assessment.title}</h3>
        <button onClick={() => { setOpen(false); reset(); }} className="rounded-full bg-muted px-4 py-1.5 text-sm font-extrabold text-muted-foreground hover:text-primary">Close</button>
      </div>

      {result ? (
        <div className="mt-6" data-testid="quiz-result">
          <div className={`flex items-center gap-4 rounded-3xl border-2 p-6 ${result.passed ? "animate-pop border-green-300 bg-green-50" : "border-amber-300 bg-amber-50"}`}>
            <span className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-full ${result.passed ? "bg-green-100 text-green-600" : "bg-amber-100 text-secondary"}`}>
              {result.passed ? <Trophy className="h-9 w-9" strokeWidth={2.25} /> : <RefreshCw className="h-8 w-8" strokeWidth={2.5} />}
            </span>
            <div>
              <p className="font-head text-4xl font-black">{result.score_pct}%</p>
              <p className="text-sm font-bold text-muted-foreground">{result.points_earned}/{result.total_points} pts · {result.passed ? "You passed! 🎉" : `Give it another go — you need ${result.passing_score}%`}</p>
              {result.preview && <p className="mt-1 text-xs font-bold text-muted-foreground" data-testid="quiz-preview-note">Preview mode — this score was not saved.</p>}
            </div>
          </div>
          <div className="mt-6 space-y-3">
            {result.results.map((r, i) => (
              <div key={i} className={`rounded-2xl border-2 p-4 ${r.is_correct ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
                <div className="flex items-start gap-3">
                  {r.is_correct ? <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-green-600" strokeWidth={2.5} /> : <XCircle className="mt-0.5 h-6 w-6 shrink-0 text-red-500" strokeWidth={2.5} />}
                  <div className="min-w-0">
                    <p className="font-extrabold">{r.question}</p>
                    <p className="mt-1 text-sm font-bold text-muted-foreground">Your answer: <span className={r.is_correct ? "text-green-600" : "text-red-500"}>{String(r.your_answer ?? "—")}</span></p>
                    {!r.is_correct && <p className="text-sm font-bold text-muted-foreground">Correct: <span className="text-green-600">{r.correct_answer}</span></p>}
                    {r.explanation && <p className="mt-2 text-sm font-semibold text-foreground/80">{r.explanation}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={reset} className="mt-6 rounded-full bg-primary px-7 py-6 text-base font-extrabold text-white tactile hover:bg-primary" data-testid="quiz-retake"><RotateCcw className="mr-2 h-5 w-5" strokeWidth={2.5} /> Try Again</Button>
        </div>
      ) : (
        <div className="mt-6 space-y-7">
          {questions.map((q, i) => (
            <div key={i} data-testid={`quiz-q-${i}`}>
              <p className="text-lg font-extrabold"><span className="mr-2 text-primary">{i + 1}.</span>{q.question}</p>
              {q.type === "short_answer" ? (
                <Input
                  value={answers[i] || ""}
                  onChange={(e) => setAnswers({ ...answers, [i]: e.target.value })}
                  placeholder="Type your answer"
                  data-testid={`quiz-input-${i}`}
                  className="mt-3 rounded-2xl border-2 border-border bg-background py-6 text-base font-semibold"
                />
              ) : (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {(q.options || ["True", "False"]).map((opt, oi) => {
                    const selected = answers[i] === opt;
                    return (
                      <button
                        key={opt}
                        onClick={() => setAnswers({ ...answers, [i]: opt })}
                        data-testid={`quiz-opt-${i}-${opt}`}
                        className={`flex min-h-[64px] w-full items-center gap-3 rounded-2xl border-2 p-4 text-left text-base font-extrabold transition-all duration-150 hover:-translate-y-0.5 ${selected ? "border-primary bg-primary/10 text-foreground shadow-sm" : "border-border bg-card hover:border-primary/50"}`}
                      >
                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-black ${selected ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>{LETTERS[oi] || oi + 1}</span>
                        <span>{opt}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-[#22C55E] py-7 text-lg font-extrabold text-white tactile hover:bg-[#22C55E]" data-testid={`quiz-submit-${assessment.assessment_id}`}>
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : "Check My Answers!"}
          </Button>
        </div>
      )}
    </div>
  );
}
